/**
 * The row document and the row patch (PLAN §6.1, D2, D6).
 *
 *     rowId       ULID
 *     rundownId   the partition (PK RD#<rundownId>)
 *     kind        immutable after creation
 *     parentId, ord, deleted   — LWW registers in f/h, mirrored top-level
 *     f, h        values and per-field HLCs
 *     source      native | mos (with the NRCS ids), lockedFields[]   (D2)
 *     text        { <field>: Yjs state, base64 }
 *     version     optimistic concurrency, +1 per write
 *     changedAt   ULID, the catch-up cursor
 *
 * **Partitions.** Segment, part, piece and adlib rows live in their rundown's
 * partition. A rundown row IS its partition's head: `rundownId === rowId`, and
 * `GET /rundowns/{id}` returns it. A playlist row is the head of its own
 * partition the same way. A rundown's `parentId` names its playlist (or is
 * null), and its `ord` orders it within the playlist — so "the playlist's
 * rundown order" is the one ordering mechanism every other level uses, and
 * two producers reordering rundowns concurrently converge like any move
 * (an array-valued field under LWW would drop one of the two reorders).
 *
 * **The top-level `parentId`, `ord` and `deleted` are copies of `f`**, kept
 * because storage indexes them (the `?parentId=` query, PLAN §6.5) and every
 * consumer reads them. `f`/`h` are the truth; `rowSchema` rejects a row whose
 * copies disagree, and `patch.ts` is the only code that writes them.
 */
import { z } from 'zod';
import { rowKindSchema } from './fields.js';
import { base64Schema, fieldPathSchema, hlcSchema, jsonValueSchema, opaqueIdSchema, ordSchema, ulidSchema } from './primitives.js';

/**
 * Where a row came from (D2). An NRCS row carries the MOS ids that identify it
 * upstream; the gateway (§7.6, planned) matches on them.
 */
export const rowSourceSchema = z.discriminatedUnion('system', [
  z.object({ system: z.literal('native') }).strict(),
  z
    .object({
      system: z.literal('mos'),
      /** The NRCS's MOS id (`ncsID`). */
      ncsId: z.string().min(1).max(128),
      /** Running order id (`roID`). */
      roId: z.string().min(1).max(128),
      /** Story id, for rows below the running order. */
      storyId: z.string().min(1).max(128).nullable(),
      /** Item id, for rows mapped from a story item. */
      itemId: z.string().min(1).max(128).nullable(),
    })
    .strict(),
]);
export type RowSource = z.infer<typeof rowSourceSchema>;
export type SourceSystem = RowSource['system'];

export const rowSchema = z
  .object({
    rowId: ulidSchema,
    rundownId: ulidSchema,
    kind: rowKindSchema,
    parentId: ulidSchema.nullable(),
    ord: ordSchema,
    deleted: z.boolean(),
    f: z.record(fieldPathSchema, jsonValueSchema),
    h: z.record(fieldPathSchema, hlcSchema),
    source: rowSourceSchema,
    /** Field paths (or namespace prefixes) only `source.system` may write. */
    lockedFields: z.array(fieldPathSchema),
    text: z.record(fieldPathSchema, base64Schema),
    version: z.number().int().nonnegative(),
    changedAt: ulidSchema,
  })
  .strict()
  .refine((row) => sameKeys(row.f, row.h), { message: 'f and h must name the same fields' })
  .refine(structuralCopiesAgree, { message: 'parentId/ord/deleted must equal f.parentId/f.ord/f.deleted' });

export type Row = z.infer<typeof rowSchema>;

function sameKeys(a: Readonly<Record<string, unknown>>, b: Readonly<Record<string, unknown>>): boolean {
  const ka = Object.keys(a);
  return ka.length === Object.keys(b).length && ka.every((k) => Object.hasOwn(b, k));
}

function structuralCopiesAgree(row: {
  readonly parentId: string | null;
  readonly ord: string;
  readonly deleted: boolean;
  readonly f: Readonly<Record<string, unknown>>;
}): boolean {
  return row.f['parentId'] === row.parentId && row.f['ord'] === row.ord && row.f['deleted'] === row.deleted;
}

/**
 * What the first patch for a row must carry besides `set.parentId` and
 * `set.ord`: the immutable identity, and the ownership (D2). A re-delivered
 * creation patch is recognised by `kind` and `rundownId` matching.
 */
export const rowCreateSchema = z
  .object({
    kind: rowKindSchema,
    rundownId: ulidSchema,
    source: rowSourceSchema,
    lockedFields: z.array(fieldPathSchema),
  })
  .strict();
export type RowCreate = z.infer<typeof rowCreateSchema>;

/**
 * One patch: set fields of one row under one HLC (PLAN §6.1:
 * `{rowId, set, hlc, origin, clientOpId}`), plus:
 *   - `text`: Yjs updates for text fields, base64 (PLAN §6.1 "Text fields");
 *   - `create`: present on the patch that creates the row.
 *
 * `.strict()`: an unknown key is a client bug or a version skew, and silently
 * dropping it would lose a write without a trace. Additive evolution (§6.8)
 * happens inside `set`, whose keys are field paths.
 */
export const rowPatchSchema = z
  .object({
    rowId: ulidSchema,
    set: z.record(fieldPathSchema, jsonValueSchema),
    text: z.record(fieldPathSchema, base64Schema).optional(),
    create: rowCreateSchema.optional(),
    hlc: hlcSchema,
    /** The writer that produced it (node id, session id), so fan-out can skip the origin. */
    origin: opaqueIdSchema,
    /** Client-generated, so a retried send is recognisable as the same operation. */
    clientOpId: opaqueIdSchema,
  })
  .strict()
  .refine((p) => Object.keys(p.set).length > 0 || Object.keys(p.text ?? {}).length > 0, {
    message: 'a patch must set at least one field or carry a text update',
  });

export type RowPatch = z.infer<typeof rowPatchSchema>;
